/**
 * Editor Store - Zustand 狀態管理
 *
 * 管理編輯器的文件狀態、UI 狀態和 Undo/Redo 堆疊
 *
 * @module editor/store/editorStore
 * @see Requirements 8.1, 8.4, 8.5
 */

import { create } from 'zustand';
import type {
  EditorMode,
  EditorSelection,
  EditorErrorInfo,
  UndoEntry,
  SetMarkdownOptions,
} from '../types';

// ============================================================================
// Constants
// ============================================================================

/** 預設 Undo 堆疊深度 */
const DEFAULT_UNDO_DEPTH = 100;

/** Undo 群組超時時間 (ms) - 在此時間內的變更會被合併 */
const UNDO_GROUP_TIMEOUT_MS = 500;

// ============================================================================
// State Interface
// ============================================================================

/**
 * 編輯器狀態介面
 */
export interface EditorState {
  // ─────────────────────────────────────────────────────────────────────────
  // Document State (文件狀態)
  // ─────────────────────────────────────────────────────────────────────────

  /** 文件 ID */
  docId: string | null;

  /** Markdown 內容 */
  markdown: string;

  /** 版本向量 (單調遞增計數器，用於變更偵測) */
  versionVector: number;

  /** 是否有未儲存的變更 */
  isDirty: boolean;

  // ─────────────────────────────────────────────────────────────────────────
  // UI State (UI 狀態)
  // ─────────────────────────────────────────────────────────────────────────

  /** 編輯模式 */
  mode: EditorMode;

  /** 是否正在載入 */
  isLoading: boolean;

  /** 是否為唯讀模式 */
  isReadonly: boolean;

  /** 是否離線 */
  isOffline: boolean;

  /** 錯誤資訊 */
  error: EditorErrorInfo | null;

  // ─────────────────────────────────────────────────────────────────────────
  // Undo/Redo State
  // ─────────────────────────────────────────────────────────────────────────

  /** Undo 堆疊 */
  undoStack: UndoEntry[];

  /** Redo 堆疊 */
  redoStack: UndoEntry[];

  /** Undo 堆疊深度上限 */
  undoDepth: number;

  /** 當前選取範圍 */
  selection: EditorSelection;

  /** 最後一次 Undo 條目的時間戳 (用於群組合併) */
  lastUndoTimestamp: number;

  // ─────────────────────────────────────────────────────────────────────────
  // Actions
  // ─────────────────────────────────────────────────────────────────────────

  /** 設定 Markdown 內容 */
  setMarkdown: (markdown: string, options?: SetMarkdownOptions) => void;

  /** 設定編輯模式 */
  setMode: (mode: EditorMode) => void;

  /** 設定載入狀態 */
  setLoading: (isLoading: boolean) => void;

  /** 設定唯讀狀態 */
  setReadonly: (isReadonly: boolean) => void;

  /** 設定離線狀態 */
  setOffline: (isOffline: boolean) => void;

  /** 設定錯誤資訊 */
  setError: (error: EditorErrorInfo | null) => void;

  /** 設定選取範圍 */
  setSelection: (selection: EditorSelection) => void;

  /** 執行 Undo */
  undo: () => boolean;

  /** 執行 Redo */
  redo: () => boolean;

  /** 推入 Undo 條目 */
  pushUndo: (entry: Omit<UndoEntry, 'timestamp'>) => void;

  /** 清除 Undo 歷史 */
  clearUndoHistory: () => void;

  /** 初始化文件 */
  initDocument: (docId: string, markdown: string) => void;

  /** 重置狀態 */
  reset: () => void;

  /** 標記為已儲存 (清除 dirty 狀態) */
  markSaved: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialSelection: EditorSelection = {
  anchor: 0,
  head: 0,
  ranges: [{ from: 0, to: 0 }],
};

const initialState = {
  // Document State
  docId: null,
  markdown: '',
  versionVector: 0,
  isDirty: false,

  // UI State
  mode: 'plain' as EditorMode,
  isLoading: false,
  isReadonly: false,
  isOffline: false,
  error: null,

  // Undo/Redo State
  undoStack: [] as UndoEntry[],
  redoStack: [] as UndoEntry[],
  undoDepth: DEFAULT_UNDO_DEPTH,
  selection: initialSelection,
  lastUndoTimestamp: 0,
};

// ============================================================================
// Store Creation
// ============================================================================

/**
 * 編輯器 Store
 *
 * 使用 Zustand 管理編輯器狀態，支援：
 * - 文件狀態追蹤 (docId, markdown, versionVector, isDirty)
 * - UI 狀態管理 (mode, isLoading, isReadonly, isOffline, error)
 * - Undo/Redo 堆疊管理 (支援群組合併)
 *
 * @example
 * ```tsx
 * const markdown = useEditorStore((state) => state.markdown);
 * const setMarkdown = useEditorStore((state) => state.setMarkdown);
 *
 * // 更新內容
 * setMarkdown('# Hello World');
 *
 * // Undo
 * const undo = useEditorStore((state) => state.undo);
 * undo();
 * ```
 */
export const useEditorStore = create<EditorState>()((set, get) => ({
  ...initialState,

  // ─────────────────────────────────────────────────────────────────────────
  // Document Actions
  // ─────────────────────────────────────────────────────────────────────────

  setMarkdown: (markdown: string, options?: SetMarkdownOptions) => {
    const state = get();
    const preserveUndo = options?.preserveUndo ?? true;

    // 如果內容沒有變化，不做任何事
    if (markdown === state.markdown) {
      return;
    }

    // 推入 Undo 條目 (如果需要保留歷史)
    if (preserveUndo && state.markdown !== '') {
      const now = Date.now();
      const shouldGroup =
        now - state.lastUndoTimestamp < UNDO_GROUP_TIMEOUT_MS && state.undoStack.length > 0;

      if (shouldGroup) {
        // 合併到最後一個 Undo 條目 (不新增新條目)
        // 保持原始狀態，只更新時間戳
        set({ lastUndoTimestamp: now });
      } else {
        // 新增 Undo 條目
        const entry: UndoEntry = {
          markdown: state.markdown,
          selection: state.selection,
          timestamp: now,
        };

        const newUndoStack = [...state.undoStack, entry].slice(-state.undoDepth);

        set({
          undoStack: newUndoStack,
          redoStack: [], // 清除 Redo 堆疊
          lastUndoTimestamp: now,
        });
      }
    }

    set({
      markdown,
      versionVector: state.versionVector + 1,
      isDirty: true,
    });
  },

  setMode: (mode: EditorMode) => {
    set({ mode });
  },

  setLoading: (isLoading: boolean) => {
    set({ isLoading });
  },

  setReadonly: (isReadonly: boolean) => {
    set({ isReadonly });
  },

  setOffline: (isOffline: boolean) => {
    set({ isOffline });
  },

  setError: (error: EditorErrorInfo | null) => {
    set({ error });
  },

  setSelection: (selection: EditorSelection) => {
    set({ selection });
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Undo/Redo Actions
  // ─────────────────────────────────────────────────────────────────────────

  undo: () => {
    const state = get();

    if (state.undoStack.length === 0) {
      return false;
    }

    // 取出最後一個 Undo 條目
    const entry = state.undoStack[state.undoStack.length - 1];
    if (!entry) {
      return false;
    }
    const newUndoStack = state.undoStack.slice(0, -1);

    // 將當前狀態推入 Redo 堆疊
    const redoEntry: UndoEntry = {
      markdown: state.markdown,
      selection: state.selection,
      timestamp: Date.now(),
    };

    set({
      markdown: entry.markdown,
      selection: entry.selection,
      undoStack: newUndoStack,
      redoStack: [...state.redoStack, redoEntry],
      versionVector: state.versionVector + 1,
      isDirty: true,
    });

    return true;
  },

  redo: () => {
    const state = get();

    if (state.redoStack.length === 0) {
      return false;
    }

    // 取出最後一個 Redo 條目
    const entry = state.redoStack[state.redoStack.length - 1];
    if (!entry) {
      return false;
    }
    const newRedoStack = state.redoStack.slice(0, -1);

    // 將當前狀態推入 Undo 堆疊
    const undoEntry: UndoEntry = {
      markdown: state.markdown,
      selection: state.selection,
      timestamp: Date.now(),
    };

    set({
      markdown: entry.markdown,
      selection: entry.selection,
      undoStack: [...state.undoStack, undoEntry],
      redoStack: newRedoStack,
      versionVector: state.versionVector + 1,
      isDirty: true,
    });

    return true;
  },

  pushUndo: (entry: Omit<UndoEntry, 'timestamp'>) => {
    const state = get();
    const now = Date.now();

    const fullEntry: UndoEntry = {
      ...entry,
      timestamp: now,
    };

    const newUndoStack = [...state.undoStack, fullEntry].slice(-state.undoDepth);

    set({
      undoStack: newUndoStack,
      redoStack: [], // 清除 Redo 堆疊
      lastUndoTimestamp: now,
    });
  },

  clearUndoHistory: () => {
    set({
      undoStack: [],
      redoStack: [],
      lastUndoTimestamp: 0,
    });
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Document Lifecycle Actions
  // ─────────────────────────────────────────────────────────────────────────

  initDocument: (docId: string, markdown: string) => {
    set({
      docId,
      markdown,
      versionVector: 0,
      isDirty: false,
      undoStack: [],
      redoStack: [],
      lastUndoTimestamp: 0,
      error: null,
      selection: initialSelection,
    });
  },

  reset: () => {
    set(initialState);
  },

  markSaved: () => {
    set({ isDirty: false });
  },
}));

// ============================================================================
// Selector Helpers
// ============================================================================

/**
 * 選取文件狀態
 */
export const selectDocumentState = (state: EditorState) => ({
  docId: state.docId,
  markdown: state.markdown,
  versionVector: state.versionVector,
  isDirty: state.isDirty,
});

/**
 * 選取 UI 狀態
 */
export const selectUIState = (state: EditorState) => ({
  mode: state.mode,
  isLoading: state.isLoading,
  isReadonly: state.isReadonly,
  isOffline: state.isOffline,
  error: state.error,
});

/**
 * 選取 Undo/Redo 狀態
 */
export const selectUndoState = (state: EditorState) => ({
  canUndo: state.undoStack.length > 0,
  canRedo: state.redoStack.length > 0,
  undoCount: state.undoStack.length,
  redoCount: state.redoStack.length,
});
